import { useState, useEffect } from 'react'
import Flex, { SimpleFlex } from '@eversports/react-components/primitives/Flex'
import Processing from '@eversports/react-components/primitives/Processing'
import Switch from '@eversports/react-components/primitives/Switch'
import { useLocation } from 'react-router-dom'
import Text from '@eversports/react-components/primitives/Text'
import Dialog, { DialogContent, DialogTitle } from '@eversports/react-components/primitives/Dialog'
import UnorderedList from '@eversports/react-components/primitives/UnorderedList'
import Link from '@eversports/react-components/primitives/Link'
import { makeStyles } from '@mui/styles'
import getPhoenixUrlParams from '../../lib/get-phoenix-url-params'
import { useCorporateBenefitsSetBenefitsOnCartMutation, useCorporateInfoItemCartQuery } from '../../graphql'

import { Localized, useIntl } from '../../localization/react'

const useStyles = makeStyles({
  clickableText: {
    textDecoration: 'underline',
    '&:hover': {
      cursor: 'pointer',
    },
  },

  wrapper: {
    overflowX: 'hidden',
    marginTop: '8px',
  },
})

const CorporateBenefitsToggle = () => {
  const [showToggle, setShowToggle] = useState(false)
  const [isChecked, setIsChecked] = useState(true)
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false)

  const intl = useIntl()
  const classes = useStyles()
  const location = useLocation()
  const { cartId, orderId } = getPhoenixUrlParams(location.search)

  const { data: corporateInfoData, loading: isCorporateInfoLoading } = useCorporateInfoItemCartQuery({
    variables: { cartId: cartId as string },
    skip: Boolean(orderId) || !cartId,
  })

  const corporatePartnerName = corporateInfoData?.cart.availableCorporateBenefits.corporatePartner?.name

  const [setUseCorporateBenefits, { loading }] = useCorporateBenefitsSetBenefitsOnCartMutation({
    variables: { cartId: cartId as string, useBenefits: isChecked },
  })

  const handleChange = async () => {
    await setUseCorporateBenefits({ variables: { useBenefits: !isChecked, cartId: cartId as string } })
    setIsChecked(!isChecked)
  }

  const handleDialogOpen = () => {
    setIsLearnMoreOpen(true)
  }

  const handleDialogClose = () => {
    setIsLearnMoreOpen(false)
  }

  useEffect(() => {
    const hasBudgetAvailable = corporateInfoData?.cart.availableCorporateBenefits.isBudgetAvailable
    const hasSpecialPriceAvailable = corporateInfoData?.cart.availableCorporateBenefits.isSpecialPriceAvailable

    if (hasBudgetAvailable || hasSpecialPriceAvailable) {
      setShowToggle(true)
      return
    }

    setShowToggle(false)
  }, [corporateInfoData])

  if (!showToggle || !corporatePartnerName || (!cartId && !orderId) || orderId) {
    return null
  }

  return (
    <>
      <Processing isLoading={loading || isCorporateInfoLoading}>
        <Flex className={classes.wrapper} justifyContent="space-between" alignItems="center">
          <SimpleFlex flexDirection="column">
            <Text>
              <Localized id="corporate-benefits-pricing-toggle" params={{ corporatePartnerName }} />
            </Text>
            <Text className={classes.clickableText} color="primary.main" size="small" onClick={handleDialogOpen}>
              <Localized id="corporate-benefits-learn-more" />
            </Text>
          </SimpleFlex>
          <div style={{ marginRight: '-8px' }}>
            <Switch
              checked={isChecked}
              onChange={handleChange}
              inputProps={{
                'aria-label': intl.corporateBenefitsPricingToggleAriaLabel(),
              }}
            />
          </div>
        </Flex>
      </Processing>
      <Dialog
        open={isLearnMoreOpen}
        onClose={handleDialogClose}
        sx={{
          '& .MuiDialog-paper': {
            padding: '32px 4px',
          },
        }}
      >
        <DialogTitle textAlign="center">
          <Localized id="corporate-budget-shared-information-title" />
        </DialogTitle>
        <DialogContent>
          <Flex flexDirection="column" verticalSpacing="default">
            <Text>
              <Localized id="corporate-budget-shared-information-text" />
            </Text>
            <UnorderedList bulletSize="x-small">
              <Text>
                <Localized id="corporate-budget-shared-information-email" />
              </Text>
              <Text>
                <Localized id="corporate-budget-shared-information-name" />
              </Text>
              <Text>
                <Localized id="corporate-budget-shared-information-date" />
              </Text>
              <Text>
                <Localized id="corporate-budget-shared-information-amount" />
              </Text>
            </UnorderedList>
            <Text>
              <Localized id="corporate-budget-shared-information-purpose" params={{ corporatePartnerName }} />
            </Text>
            <Text>
              <Localized id="corporate-budget-shared-information-legal-basis" />
            </Text>
            <Text>
              <Localized
                id="corporate-budget-shared-information-data-protection-link"
                params={{
                  link: (
                    <Link to="/h/privacy" external blank>
                      <Localized id="eversports-data-protection-declaration" />
                    </Link>
                  ),
                }}
              />
            </Text>
          </Flex>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CorporateBenefitsToggle
